import { useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { Editor as TiptapEditor, JSONContent } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { useTranslation } from '@yorukiri/i18n';
import 'katex/dist/katex.min.css';
import { CardBlock } from './extensions/CardBlock';
import { Cloze } from './extensions/Cloze';
import { Callout } from './extensions/Callout';
import { Embed, extractIframeSrc, isEmbeddableUrl, resolveEmbed } from './extensions/Embed';
import { BlockMath, InlineMath } from './extensions/Math';
import { Media } from './extensions/Media';
import { PageLink } from './extensions/PageLink';
import { createSlashCommand, type SlashCommandItem } from './extensions/SlashCommand';
import { uploadMedia } from '../lib/mediaUpload';
import { SlashMenu, type SlashMenuHandle } from './SlashMenu';
import { EditorStyles } from './EditorStyles';

type Props = {
  initialContent?: JSONContent;
  onChange?: (doc: JSONContent) => void;
  onMount?: (editor: TiptapEditor) => void;
  onGenerateFromSelection?: (text: string) => void;
  onCreateSubpage?: (title: string) => Promise<{ id: string; title: string } | null>;
  onOpenPage?: (pageId: string) => void;
  placeholder?: string;
};

export function Editor({
  initialContent,
  onChange,
  onMount,
  onGenerateFromSelection,
  onCreateSubpage,
  onOpenPage,
  placeholder,
}: Props) {
  const { t } = useTranslation('page');
  const slashPopup = useRef<HTMLDivElement | null>(null);
  const slashRoot = useRef<Root | null>(null);
  const slashHandle = useRef<SlashMenuHandle | null>(null);
  const onCreateSubpageRef = useRef(onCreateSubpage);
  onCreateSubpageRef.current = onCreateSubpage;

  const slashExtension = createSlashCommand(
    () => {
    return {
      onStart: (props) => {
        const el = document.createElement('div');
        el.style.position = 'absolute';
        el.style.zIndex = '50';
        document.body.appendChild(el);
        slashPopup.current = el;
        const root = createRoot(el);
        slashRoot.current = root;
        const rect = props.clientRect?.();
        if (rect) positionPopup(el, rect);
        root.render(
          <SlashMenu
            ref={(h) => {
              slashHandle.current = h;
            }}
            items={props.items}
            command={(item) => props.command(item)}
          />,
        );
      },
      onUpdate: (props) => {
        const el = slashPopup.current;
        if (!el || !slashRoot.current) return;
        const rect = props.clientRect?.();
        if (rect) positionPopup(el, rect);
        slashRoot.current.render(
          <SlashMenu
            ref={(h) => {
              slashHandle.current = h;
            }}
            items={props.items}
            command={(item) => props.command(item)}
          />,
        );
      },
      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          slashPopup.current?.remove();
          slashPopup.current = null;
          return true;
        }
        return slashHandle.current?.onKeyDown(props.event) ?? false;
      },
      onExit: () => {
        slashRoot.current?.unmount();
        slashRoot.current = null;
        slashPopup.current?.remove();
        slashPopup.current = null;
      },
    };
  },
    {
      onCreateSubpage: onCreateSubpage
        ? (title) => onCreateSubpageRef.current!(title)
        : undefined,
    },
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({
        placeholder: ({ node }) =>
          node.type.name === 'heading'
            ? t('editor.heading_placeholder')
            : (placeholder ?? t('editor.placeholder')),
      }),
      Cloze,
      Callout,
      CardBlock,
      InlineMath,
      BlockMath,
      Media,
      Embed,
      PageLink,
      slashExtension,
    ],
    content: initialContent ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate: ({ editor }) => onChange?.(editor.getJSON()),
    editorProps: {
      attributes: { class: 'tiptap' },
      handleClickOn: (_view, _pos, node) => {
        if (node.type.name !== 'pageLink') return false;
        const id = node.attrs.pageId as string | undefined;
        if (id && onOpenPage) {
          onOpenPage(id);
          return true;
        }
        return false;
      },
      // Drop + paste: if the data transfer carries files we can host, upload
      // them and insert media nodes. Unsupported files fall through to the
      // default handler so the user can still drop images of our custom blocks.
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (files.length === 0) return false;
        const uploadable = files.filter(isUploadable);
        if (uploadable.length === 0) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        const insertAt = coords?.pos ?? view.state.selection.from;
        void uploadFilesAndInsert(view, uploadable, insertAt);
        return true;
      },
      handlePaste: (view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []).filter(isUploadable);
        if (files.length > 0) {
          event.preventDefault();
          void uploadFilesAndInsert(view, files, view.state.selection.from);
          return true;
        }
        const html = event.clipboardData?.getData('text/html');
        const text = event.clipboardData?.getData('text/plain');

        // (1) User pasted an iframe snippet — what Figma/Loom/YouTube's
        // "Copy embed code" buttons put on the clipboard. Could arrive as
        // HTML content or as plain text (when the clipboard only carries
        // a string representation of the snippet).
        const iframeSrc =
          (html && extractIframeSrc(html)) ??
          (text && /<iframe/i.test(text) ? extractIframeSrc(text) : null);
        if (iframeSrc) {
          event.preventDefault();
          const { provider } = resolveEmbed(iframeSrc);
          const node = view.state.schema.nodes.embed!.create({
            url: iframeSrc,
            provider,
          });
          view.dispatch(view.state.tr.replaceSelectionWith(node));
          return true;
        }

        // (2) Bare URL paste → embed if a known provider recognises the host.
        // Only fires when there's no accompanying HTML so rich pastes keep
        // their formatting.
        if (!text || html) return false;
        const url = isEmbeddableUrl(text);
        if (!url) return false;
        event.preventDefault();
        const { provider } = resolveEmbed(url);
        const node = view.state.schema.nodes.embed!.create({ url, provider });
        view.dispatch(view.state.tr.replaceSelectionWith(node));
        return true;
      },
    },
  });

  useEffect(
    () => () => {
      slashRoot.current?.unmount();
      slashPopup.current?.remove();
    },
    [],
  );

  useEffect(() => {
    if (editor) onMount?.(editor);
  }, [editor, onMount]);

  if (!editor) return null;

  const handleGenerate = () => {
    const { from, to, empty } = editor.state.selection;
    if (empty) return;
    const text = editor.state.doc.textBetween(from, to, '\n', ' ');
    onGenerateFromSelection?.(text);
  };

  return (
    <>
      <EditorStyles />
      <BubbleMenu
        editor={editor}
        options={{ placement: 'top' }}
        className="tiptap-bubble"
        // Only show when real text is selected. NodeSelections around atom
        // nodes (images, embeds, media, cardBlock) report a non-empty range
        // but carry no text content, so `textBetween` is the cleanest filter.
        shouldShow={({ state, from, to }) => {
          if (from === to) return false;
          const text = state.doc.textBetween(from, to, '\n', ' ').trim();
          return text.length > 0;
        }}
      >
        <BubbleButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <b>B</b>
        </BubbleButton>
        <BubbleButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <i>I</i>
        </BubbleButton>
        <BubbleButton active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
          <code>&lt;/&gt;</code>
        </BubbleButton>
        <BubbleButton active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <s>S</s>
        </BubbleButton>
        {onGenerateFromSelection && (
          <BubbleButton onClick={handleGenerate} title="Generate cards from selection">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              ✦ <span style={{ fontSize: 11, letterSpacing: '0.04em' }}>Gen</span>
            </span>
          </BubbleButton>
        )}
      </BubbleMenu>
      <EditorContent editor={editor} />
    </>
  );
}

function BubbleButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={active ? 'active' : ''}
    >
      {children}
    </button>
  );
}

function positionPopup(el: HTMLElement, rect: DOMRect) {
  const gap = 6;
  const menuH = Math.min(el.offsetHeight || 320, 360);
  const spaceBelow = window.innerHeight - rect.bottom;
  const top = spaceBelow > menuH + gap ? rect.bottom + gap : rect.top - menuH - gap;
  el.style.top = `${Math.max(8, top)}px`;
  el.style.left = `${Math.min(rect.left, window.innerWidth - 260)}px`;
}

function isUploadable(file: File): boolean {
  return (
    file.type.startsWith('image/') ||
    file.type.startsWith('audio/') ||
    file.type === 'application/pdf'
  );
}

function kindFor(file: File): 'image' | 'audio' | 'pdf' {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'pdf';
}

// Uploads each file sequentially (avoids flooding the worker) and inserts a
// media node at `insertAt`. The insertion point is advanced as nodes land so
// multiple drops preserve order.
async function uploadFilesAndInsert(
  view: import('@tiptap/pm/view').EditorView,
  files: File[],
  insertAt: number,
) {
  let pos = insertAt;
  for (const file of files) {
    try {
      const res = await uploadMedia(file);
      const node = view.state.schema.nodes.media!.create({
        mediaId: res.id,
        kind: kindFor(file),
        alt: file.name,
        contentType: res.contentType,
        name: res.originalName,
      });
      const tr = view.state.tr.insert(pos, node);
      view.dispatch(tr);
      pos += node.nodeSize;
    } catch (err) {
      // Surface via console for now; a toast system would hook in here.
      // eslint-disable-next-line no-console
      console.error('Media upload failed', err);
    }
  }
  view.focus();
}

export type { SlashCommandItem };
